import React from 'react';
import { useTranslation } from 'react-i18next';

import { FILTRATION_TYPES, SHOW_CHILD_ENTITIES } from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useAppSelector } from 'hooks/storeHooks';
import { IFilterSetting, INullable } from 'utils/types';

import { useFilterContext } from '../../../FilterContext';
import {
  ANALYTICS_DICTIONARY_PREFIX,
  AUTH_DICTIONARY_PREFIX,
  BUILDER_DICTIONARY_PREFIX,
  DICTIONARY_PREFIX,
  FILTRATION_ICONS,
  ROLE_SCOPE,
} from '../../../utils/constants';
import { getStrategies } from '../../../utils/helpers';
import { FILTER_OPERATIONS } from '../../../utils/types';
import FilterOperationButton from '../FilterOperationButton';
import { StyledButton } from '../FilterOperationButton/styled';

import AdditionalCondition from './AdditionalCondition';
import FilterComponent from './FilterComponent';
import {
  Container,
  Filter,
  FilterElements,
  RemoveButton,
  StyledHighlightedText,
  StyledSwitch,
} from './styled';

interface IGetSelectConfigProps {
  multiSelect: boolean;
  operation?: INullable<FILTER_OPERATIONS>;
  type?: FILTRATION_TYPES;
}

export const getSelectConfig = ({
  multiSelect,
  operation,
  type,
}: IGetSelectConfigProps) => {
  const isDictionary = !!type?.startsWith(DICTIONARY_PREFIX);
  const isAnalyticsDictionary = !!type?.startsWith(ANALYTICS_DICTIONARY_PREFIX);
  const isAuthDictionary = !!type?.startsWith(AUTH_DICTIONARY_PREFIX);
  const isBuilderDictionary = !!type?.startsWith(BUILDER_DICTIONARY_PREFIX);

  const isSelect =
    type === FILTRATION_TYPES.BOOLEAN ||
    isDictionary ||
    isAnalyticsDictionary ||
    isAuthDictionary ||
    isBuilderDictionary;
  const isMultiSelect =
    multiSelect ||
    operation === FILTER_OPERATIONS.IN ||
    operation === FILTER_OPERATIONS.NOT_IN;

  return {
    isMultiSelect,
    isSelect,
  };
};

interface IFilterItem extends IFilterSetting {
  columnKey: string;
  defaultLikesFirstFields?: string[];
  searchTerm?: string;
}

const FilterItem = ({
  columnKey,
  defaultLikesFirstFields,
  isRequestFilter,
  listOperations,
  multiSelect,
  searchNullable,
  searchTerm,
  type,
}: IFilterItem) => {
  const { t } = useTranslation();

  const filterOperationsSortStrategy = useAppSelector(
    (state) => state.userConfiguration.filterOperationsSortStrategy,
  );

  const {
    config: { betweenList, fallbackValues, intlPrefix },
    onSelectFilter,
    onSelectOperation,
    removeFilter,
    sidebarFilters,
    toggleFilterEnabled,
  } = useFilterContext();

  const sidebarFilter = sidebarFilters[columnKey];

  const removeCurrentFilter = React.useCallback(() => {
    removeFilter({ columnKey, resetCanFilter: true });
  }, [removeFilter, columnKey]);

  const onTogglePower = React.useCallback(() => {
    toggleFilterEnabled({ columnKey });
  }, [toggleFilterEnabled, columnKey]);

  const { isMultiSelect, isSelect } = getSelectConfig({
    multiSelect,
    operation: sidebarFilter?.operation,
    type,
  });

  const strategiesList = React.useMemo(() => {
    return getStrategies({
      betweenList,
      columnKey,
      defaultLikesFirstFields,
      filterOperationsSortStrategy,
      isSelect: isSelect || isRequestFilter,
      listOperations,
      searchNullable,
      type,
    });
  }, [
    type,
    listOperations,
    searchNullable,
    betweenList,
    columnKey,
    isSelect,
    isRequestFilter,
    filterOperationsSortStrategy,
    defaultLikesFirstFields,
  ]);

  const [firstOperation] = strategiesList;

  const onSelectFilterOption = React.useCallback(
    (_e: React.ChangeEvent, checked: boolean) => {
      onSelectFilter({
        columnKey,
        operation: firstOperation,
        value: checked as boolean,
      });
    },
    [onSelectFilter, columnKey, firstOperation],
  );

  const currentOperation = sidebarFilter?.operation || firstOperation;

  if (columnKey === SHOW_CHILD_ENTITIES) {
    return (
      <Container>
        <StyledSwitch
          checked={!!sidebarFilter?.value}
          label={t(`${intlPrefix}.${columnKey}` as never)}
          onChange={onSelectFilterOption}
        />
      </Container>
    );
  }

  const isLikeNotLikeOperation =
    sidebarFilter?.operation === undefined
      ? firstOperation === FILTER_OPERATIONS.LIKE ||
        firstOperation === FILTER_OPERATIONS.NOT_LIKE
      : sidebarFilter?.operation === FILTER_OPERATIONS.LIKE ||
        sidebarFilter?.operation === FILTER_OPERATIONS.NOT_LIKE;

  const isChipOff = sidebarFilter?.enabled === false;
  const hasNoValueOrOperation =
    sidebarFilter?.value === undefined && sidebarFilter?.operation === undefined;
  const showRemoveButton =
    (isSelect || !isMultiSelect || isRequestFilter) &&
    !isLikeNotLikeOperation &&
    columnKey !== ROLE_SCOPE;
  const hasInlinePowerButtons =
    isLikeNotLikeOperation || (isMultiSelect && !isSelect && !isRequestFilter);

  return (
    <Container>
      <StyledHighlightedText searchTerm={searchTerm}>
        {t(`${intlPrefix}.${columnKey}` as never)}
      </StyledHighlightedText>
      <Filter>
        <FilterElements>
          <FilterOperationButton
            columnKey={columnKey}
            isDisabled={!!fallbackValues?.[columnKey]}
            onSelectOperation={onSelectOperation}
            operations={strategiesList}
            selectedOperation={sidebarFilter?.operation}
          >
            <StyledButton
              $enableShowFocus
              disabled={!!fallbackValues?.[columnKey]}
              iconSize={19}
              startIcon={FILTRATION_ICONS[sidebarFilter?.operation || strategiesList[0]]}
              testIds={{ container: `${columnKey}OperationsButton` }}
              variant='icon'
            />
          </FilterOperationButton>
          <FilterComponent
            appliedFilter={sidebarFilter}
            columnKey={columnKey}
            fallbackOperation={firstOperation}
            isRequestFilter={isRequestFilter}
            isSelect={isSelect}
            multiSelect={isMultiSelect}
            type={type}
          />
        </FilterElements>
        {columnKey !== ROLE_SCOPE && !hasInlinePowerButtons && (
          <RemoveButton
            disabled={!!fallbackValues?.[columnKey] || hasNoValueOrOperation}
            onClick={onTogglePower}
            startIcon={<ICONS_MAP.Power />}
            testIds={{ container: `${columnKey}TogglePowerButton` }}
            variant={isChipOff ? 'transparentDanger' : 'icon'}
          />
        )}
        {showRemoveButton && (
          <RemoveButton
            disabled={hasNoValueOrOperation}
            onClick={removeCurrentFilter}
            startIcon={<ICONS_MAP.Delete />}
            testIds={{ container: `${columnKey}RemoveFilterButton` }}
            variant='icon'
          />
        )}
      </Filter>
      <AdditionalCondition
        additionalCondition={sidebarFilter?.additionalCondition}
        columnKey={columnKey}
        currentOperation={currentOperation}
        isMultiSelect={isMultiSelect}
        isRequestFilter={isRequestFilter}
        isSelect={isSelect}
        listOperations={listOperations}
        type={type}
      />
    </Container>
  );
};

export default appReactMemo(FilterItem);
